// @ts-nocheck
import SCModal from '@/PageComponents/Modal/SCModal';
import SCPasswordInput from '@/components/sc-controls/form-controls/sc-password-input';
import helper from '@/utils/helper';
import {Alert, Button, Flex, Text, Title} from '@mantine/core';
import { FC, useContext, useState } from 'react';
import * as Enums from '@/utils/enums';
import ToastContext from '@/utils/toast-context';
import { logout } from '@/utils/auth';
import {IconInfoCircle} from "@tabler/icons-react";
import { showNotification } from '@mantine/notifications';
import Storage from '@/utils/storage';
import { PlatformAuthService } from '@/services/platform-auth';
import * as Sentry from '@sentry/react';

type ChangeEmployeePasswordProps = {
    /** Legacy tenant-DB AuthUser.ID — kept for backward compatibility but no longer used
     *  once targetEmail (PlatformAuth resolution) is supplied. */
    employeeID?: string;
    /** Target user's email — resolves the platform user in PlatformAuth (preferred path). */
    targetEmail?: string;
    /** Logged-in user changing their OWN password: collects the current password and calls
     *  POST /api/auth/change-password (verifies it, keeps the session alive). Never combine
     *  with targetEmail — set-password is an admin force-set that revokes every session of
     *  the target, which for a self-service change logs the user out of their own session. */
    selfService?: boolean;
    isOpen: boolean;
    setIsOpen: (val: boolean) => void;
    userwords: string[];
    title?: string;
    message?: string;
    enforced?: boolean;
}

const auth = new PlatformAuthService();

/**
 * Password-change modal with three routes:
 *  - `selfService`: the logged-in user changes their own password via PlatformAuth
 *    change-password (current-password verification, session survives).
 *  - `targetEmail`: admin/owner force-sets another user's password via PlatformAuth
 *    set-password (no current-password check, revokes the target's sessions).
 *  - `employeeID` only: legacy tenant-API `/Employee/OwnerChangePassword` fallback.
 */
const ChangeEmployeePassword: FC<ChangeEmployeePasswordProps> = ({ employeeID, targetEmail, selfService, isOpen, setIsOpen, userwords, title, message, enforced }: any) => {

    const toast = useContext(ToastContext);

    const [inputs, setInputs] = useState({
        currentPassword: "",
        ownerPassword: "",
        confirmOwnerPassword: ""
    });

    const [inputErrors, setInputErrors] = useState<any>({
    });

    const [saving, setSaving] = useState(false);

    const cookieUserwords = [
        Storage?.getCookie ? Storage.getCookie(Enums.Cookie?.servUserName) : undefined,
        Storage?.getCookie ? Storage.getCookie(Enums.Cookie?.servFullName) : undefined,
        Storage?.getCookie ? Storage.getCookie(Enums.Cookie?.servCompanyName) : undefined,
    ].filter(Boolean) as string[];
    const combinedUserwords: string[] = ((userwords && userwords.length > 0) ? userwords : []).concat(cookieUserwords)
        .filter(Boolean)
        .filter((v: any, i: any, a: any) => a.indexOf(v) === i);

    const handleInputChange = (e: any) => {
        let setter = {
            ...inputs,
            [e.name]: e.value
        };
        setInputs(setter);
    };

    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter' && !saving && e.target?.tagName !== 'BUTTON') {
            updateOwnerPassword();
        }
    };

    const updateOwnerPassword = async () => {
        setSaving(true);
        let validationItems = [
            { key: 'ownerPassword', value: inputs.ownerPassword, required: true, type: Enums.ControlType.Text },
            { key: 'confirmOwnerPassword', value: inputs.confirmOwnerPassword, required: true, type: Enums.ControlType.Text, equalsPassword: inputs.ownerPassword }
        ];
        if (selfService) {
            validationItems.push({ key: 'currentPassword', value: inputs.currentPassword, required: true, type: Enums.ControlType.Text });
        }

        let { isValid, errors } = helper.validateInputs(validationItems);

        const strengthError = helper.getPasswordStrengthError(inputs.ownerPassword, { userwords: combinedUserwords });
        if (strengthError) {
            isValid = false;
            (errors as any).ownerPassword = strengthError;
        }

        if (inputs.ownerPassword.length > 100) {
            isValid = false;
            (errors as any).ownerPassword = 'Please use a password of 100 characters or less';
        }

        setInputErrors(errors);

        if (isValid) {
            try {
                if (selfService) {
                    // Self-service: verify the current password and keep this session alive.
                    // (set-password would skip verification AND revoke our own session.)
                    if (!auth.userId) {
                        showNotification({ color: 'yellow.7', message: 'Your session has expired. Please re-login and try again.', autoClose: 5000 });
                    } else {
                        await auth.changePassword(inputs.currentPassword, inputs.ownerPassword);
                        (toast as any).setToast({
                            message: 'Your password has been changed',
                            show: true,
                            type: Enums.ToastType.success
                        });
                        setInputs({ currentPassword: "", ownerPassword: "", confirmOwnerPassword: "" });
                        setIsOpen(false);
                    }
                } else if (targetEmail) {
                    // PlatformAuth-direct path (parity with login).
                    await auth.setPassword(targetEmail, inputs.ownerPassword);
                    (toast as any).setToast({
                        message: 'Password updated',
                        show: true,
                        type: Enums.ToastType.success
                    });
                    setInputs({ ...inputs, ownerPassword: "", confirmOwnerPassword: "" });
                    setIsOpen(false);
                } else {
                    // Legacy tenant-API fallback (routed by employeeID).
                    const Fetch = (await import('@/utils/Fetch')).default;
                    const result: any = await Fetch.post({
                        url: `/Employee/OwnerChangePassword`,
                        params: { employeeID, newPassword: inputs.ownerPassword }
                    } as any);

                    const r: any = result as any;
                    if (typeof r?.HttpStatusCode === 'number') {
                        if (r.HttpStatusCode === 200) {
                            (toast as any).setToast({ message: r.Message || 'Password updated', show: true, type: Enums.ToastType.success });
                            setInputs({ ...inputs, ownerPassword: "", confirmOwnerPassword: "" });
                            setIsOpen(false);
                        } else if (r?.Message) {
                            setInputErrors({ ...errors, ownerPassword: r.Message });
                        } else {
                            showNotification({ color: 'yellow.7', message: r?.message || r?.serverMessage || 'Something went wrong', autoClose: 4000 });
                        }
                    } else if (r?.Message) {
                        setInputErrors({ ...errors, ownerPassword: r.Message });
                    } else {
                        showNotification({ color: 'yellow.7', message: r?.message || r?.serverMessage || 'Something went wrong', autoClose: 4000 });
                    }
                }
            } catch (error: any) {
                const msg = error?.message || 'Something went wrong';
                const field = selfService && /current password/i.test(msg) ? 'currentPassword' : 'ownerPassword';
                setInputErrors({ ...errors, [field]: msg });
                showNotification({ color: 'yellow.7', message: msg, autoClose: 4000 });
                // Password-strength and current-password rejections are the user's to fix.
                // A refused session, a network failure or a 5xx used to vanish behind the notification, so report those.
                if (error?.status === undefined || error.status === 401 || error.status >= 500) {
                    Sentry.captureException(error, {
                        tags: { flow: selfService ? 'change-password' : targetEmail ? 'set-password' : 'owner-change-password' },
                        extra: { status: error?.status ?? null }
                    });
                }
            }
        }

        setSaving(false);
    }

    return (<>

        <SCModal
            open={isOpen}
            size='md'
            onClose={() => { if (!enforced) setIsOpen(false); }}
            modalProps={enforced ? { closeOnClickOutside: false, closeOnEscape: false } : undefined}
        >
        <div onKeyDown={handleKeyDown}>
            <Title order={3} mb={'md'}>{title ?? (selfService ? 'Update your password' : 'Update Password for Employee')}</Title>

            {message && (
                <Alert my={'sm'} color={'teal'} icon={<IconInfoCircle size={16} />} style={{ marginBottom: 'var(--mantine-spacing-md)' }}>
                    {message}
                </Alert>
            )}

            {selfService && (
                <div className="row">
                    <div className="column">
                        <SCPasswordInput
                            onChange={handleInputChange}
                            error={inputErrors.currentPassword}
                            label={"Current password"}
                            name="currentPassword"
                            required={true}
                            value={inputs.currentPassword}
                            showPopover={false}
                        />
                    </div>
                </div>
            )}

            <div className="row">
                <div className="column">
                    <SCPasswordInput
                        onChange={handleInputChange}
                        error={inputErrors.ownerPassword}
                        label={"New password"}
                        name="ownerPassword"
                        required={true}
                        value={inputs.ownerPassword}
                        userwords={combinedUserwords}
                    />
                </div>
            </div>
            <div className="row">
                <div className="column">
                    <SCPasswordInput
                        onChange={handleInputChange}
                        error={inputErrors.confirmOwnerPassword}
                        label={"Confirm new password"}
                        name="confirmOwnerPassword"
                        required={true}
                        value={inputs.confirmOwnerPassword}
                        userwords={combinedUserwords}
                    />
                    <Text size="sm" c="dimmed" mt="sm">
                        Choose a strong password with at least 10 characters, including uppercase, lowercase, and a number. Skip common phrases like 'password', 'qwerty', '1234', or your name.
                    </Text>
                </div>
            </div>

            <Flex justify={"space-between"} mt={30}>
                <div></div>
                <div>
                {enforced ? (
                    <Button onClick={() => logout(false, '', 'password-changed')} variant='subtle' mr={'xs'}>
                        Logout
                    </Button>
                ) : (
                    <Button onClick={() => setIsOpen(false)} variant='subtle' mr={'xs'}>
                        {message ? 'Not Now' : 'Cancel'}
                    </Button>
                )}

                <Button onClick={saving ? undefined : updateOwnerPassword} disabled={saving}>
                    {saving ? "Updating" : 'Update Password'}
                </Button>
                </div>
            </Flex>
        </div>
        </SCModal>

        <style jsx>{`
             
        `}</style>
    </>);
};

export default ChangeEmployeePassword;